import { useEffect, useMemo, useState } from "react";
import {
  alfredService,
  AlfredSupportedOption,
  AlfredSupportedOptionsResponse,
} from "src/api";
import { useUrlParamsContext } from "src/contexts";

interface UseAlfredSupportedOptionsParams {
  type: "deposit" | "withdrawal";
}

/**
 * Loads the per-route currency options on mount and exposes a
 * controlled selection (`selected`, `setSelected`) the form can bind to.
 *
 * Default selection is the first option — the form can override it
 * when the user's KYC country matches one of the listed currencies
 * (we expose `selectByCurrency` for that case).
 */
export const useAlfredSupportedOptions = (
  params: UseAlfredSupportedOptionsParams,
) => {
  const { paymentRouteId } = useUrlParamsContext();
  const [response, setResponse] =
    useState<AlfredSupportedOptionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (!paymentRouteId) return;
    let cancelled = false;
    setLoading(true);
    alfredService
      .getSupportedOptions({ paymentRouteId, type: params.type })
      .then((data) => {
        if (cancelled) return;
        setResponse(data);
        if (data.options.length && !selectedCurrency) {
          setSelectedCurrency(data.options[0].source_currency);
        }
      })
      .catch((err) => {
        console.error("Failed to load Alfred supported options:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // selectedCurrency intentionally excluded so we don't reset on every
    // change; we only seed the default once the response arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRouteId, params.type]);

  const selected: AlfredSupportedOption | null = useMemo(() => {
    if (!response || !selectedCurrency) return null;
    return (
      response.options.find((o) => o.source_currency === selectedCurrency) ??
      null
    );
  }, [response, selectedCurrency]);

  return {
    options: response?.options ?? [],
    selected,
    selectedCurrency,
    setSelectedCurrency,
    loading,
  };
};
